import { ApiError, TurnResponse, TurnStreamSay, type TutorIntent } from '@pen/shared';
import { ApiRequestError } from '../api';

export interface TutorTurnPayload { ink: string; process: string; message: string | null; intent?: TutorIntent; confidence?: number | null }

/** POST SSE uses cumulative `say` snapshots; only a validated final reply commits a turn. */
export async function sendTutorTurn(attemptId: string, payload: TutorTurnPayload, onSay: (text: string) => void, signal?: AbortSignal): Promise<TurnResponse> {
  const response = await fetch(`/api/attempts/${encodeURIComponent(attemptId)}/turns`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(payload), signal,
  });
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event('pen:unauthenticated'));
    const parsed = ApiError.safeParse(await response.json().catch(() => null));
    throw new ApiRequestError(response.status, parsed.success ? parsed.data.error : 'server_error', parsed.success ? parsed.data.message ?? parsed.data.error.replaceAll('_', ' ') : `Request failed (${response.status})`);
  }
  // Older servers may ignore Accept. Never send a second POST for this fallback.
  if (!response.headers.get('content-type')?.toLowerCase().includes('text/event-stream')) return TurnResponse.parse(await response.json());
  if (!response.body) throw new Error('The tutor connection closed without a reply. Please retry.');
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let buffer = '', event = '', data: string[] = [], result: TurnResponse | null = null;
  const dispatch = () => {
    if (!data.length) { event = ''; return; }
    const value: unknown = JSON.parse(data.join('\n'));
    if (event === 'say') onSay(TurnStreamSay.parse(value).text);
    else if (event === 'reply') result = TurnResponse.parse(value);
    else if (event === 'error') {
      const failure = ApiError.parse(value);
      throw new ApiRequestError(response.status, failure.error, failure.message ?? failure.error.replaceAll('_', ' '));
    }
    event = ''; data = [];
  };
  const line = (text: string) => {
    if (!text) { dispatch(); return; }
    if (text.startsWith(':')) return;
    const colon = text.indexOf(':');
    const field = colon < 0 ? text : text.slice(0, colon);
    const value = colon < 0 ? '' : text.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  };
  try {
    while (!result) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      let index: number;
      while ((index = buffer.search(/[\r\n]/)) >= 0) {
        // A CRLF delimiter itself can straddle network chunks.
        if (!done && buffer[index] === '\r' && index === buffer.length - 1) break;
        const text = buffer.slice(0, index), width = buffer[index] === '\r' && buffer[index + 1] === '\n' ? 2 : 1;
        buffer = buffer.slice(index + width); line(text);
        if (result) break;
      }
      if (done && !result) { if (buffer) line(buffer); dispatch(); break; }
    }
    if (!result) throw new Error('The tutor connection closed before finishing. Your drawing is safe; retry this turn.');
    return result;
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
